import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users2, ShieldCheck, ChevronRight, Lock, Sparkles } from "lucide-react";

/**
 * Two-message rotating announcement banner shown site-wide above the header.
 * Message A → hiring companies (original value prop)
 * Message B → professionals / guards (ERO/SIA minimum rates enforced, on-time pay, video-agreed terms, SOPs, events)
 */
const MESSAGES = [
  {
    id: "promo-2027",
    icon: Sparkles,
    eyebrow: "Launch offer",
    body: "Free sign-up for companies before January 2027 — no registration fee, no credit card. Only pay when you book.",
    ctaLabel: "Claim free sign-up",
    ctaTo: "/register/company",
    accent: "text-gold-300",
    testId: "banner-msg-promo",
  },
  {
    id: "companies",
    icon: Users2,
    eyebrow: "For companies",
    body: "Immediate deployment of talented guards — Ireland & UK. One transparent fee. No auction, no race-to-the-bottom.",
    ctaLabel: "Search staff",
    ctaTo: "/register/company",
    accent: "text-brand",
    testId: "banner-msg-companies",
  },
  {
    id: "teams",
    icon: Users2,
    eyebrow: "Cluster teams",
    body: "Increased performance from ready-made cluster teams — kit, comms, medical & vehicles combined. One booking, one team, one contract.",
    ctaLabel: "Browse teams",
    ctaTo: "/cluster-teams",
    accent: "text-brand",
    testId: "banner-msg-teams",
  },
  {
    id: "jobs",
    icon: ShieldCheck,
    eyebrow: "Jobs board",
    body: "ERO minimum rates or above only. Jobs posted below the Employment Regulation Order rate are rejected automatically. No undercutting.",
    ctaLabel: "See jobs",
    ctaTo: "/security-officers",
    accent: "text-gold-300",
    testId: "banner-msg-jobs",
  },
  {
    id: "gdpr",
    icon: Lock,
    eyebrow: "GDPR compliant",
    body: "Personnel details are locked and numbers-only until a job is accepted. Full name, phone and address stay hidden — the industry standard for privacy.",
    ctaLabel: "How it works",
    ctaTo: "/trust",
    accent: "text-brand",
    testId: "banner-msg-gdpr",
  },
  {
    id: "match",
    icon: Sparkles,
    eyebrow: "Smart matching",
    body: "Manage your availability calendar — our algorithm matches jobs to you based on distance, qualification, kit, rating and availability.",
    ctaLabel: "Join as a Pro",
    ctaTo: "/register/staff",
    accent: "text-gold-300",
    testId: "banner-msg-match",
  },
  {
    id: "known",
    icon: Sparkles,
    eyebrow: "Career acceleration",
    body: "Get known in the industry faster. Every verified booking, endorsement and cluster invite compounds your visibility — opportunities start finding you.",
    ctaLabel: "Join as a Pro",
    ctaTo: "/register/staff",
    accent: "text-gold-300",
    testId: "banner-msg-known",
  },
  {
    id: "pros",
    icon: ShieldCheck,
    eyebrow: "For professionals",
    body: "Fair rates. On-time payments. Terms agreed on secure video call. SOPs shared in advance. Fair treatment on festivals & events. We vet companies as strictly as we vet you.",
    ctaLabel: "Join as a Pro",
    ctaTo: "/register/staff",
    accent: "text-gold-300",
    testId: "banner-msg-pros",
  },
];

export default function RotatingBanner() {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % MESSAGES.length), 7000);
    return () => clearInterval(t);
  }, []);

  const m = MESSAGES[idx];
  const Icon = m.icon;

  return (
    <div
      className="w-full bg-navy-900 border-b border-white/10 relative overflow-hidden"
      data-testid="rotating-banner"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5 flex items-center gap-3">
        <span
          className={`hidden sm:inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-outfit font-bold px-2 py-0.5 rounded-full border border-white/10 bg-white/5 flex-shrink-0 ${m.accent}`}
          data-testid={m.testId}
        >
          <Icon className="w-3 h-3" strokeWidth={2.2} />
          {m.eyebrow}
        </span>
        <div
          key={m.id}
          className="flex-1 text-xs sm:text-sm text-slate-200 font-manrope leading-snug banner-fade min-w-0"
        >
          <span className="line-clamp-2 sm:line-clamp-1">{m.body}</span>
        </div>
        <Link
          to={m.ctaTo}
          className={`hidden sm:inline-flex items-center gap-1 text-xs font-outfit font-bold uppercase tracking-widest whitespace-nowrap flex-shrink-0 hover:underline ${m.accent}`}
          data-testid={`banner-cta-${m.id}`}
        >
          {m.ctaLabel} <ChevronRight className="w-3.5 h-3.5" />
        </Link>
        <div className="hidden sm:flex items-center gap-1 flex-shrink-0" aria-hidden="true">
          {MESSAGES.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setIdx(i)}
              className={`h-1 rounded-full transition-all ${i === idx ? "w-6 bg-[#0A0A0A]" : "w-2 bg-white/30 hover:bg-white/50"}`}
              aria-label={`Show message ${i + 1}`}
              data-testid={`banner-dot-${i}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
